import React from 'react';
import styled from 'styled-components';
import { FaClock, FaEuroSign } from 'react-icons/fa';

const PricingSection = styled.section`
  padding: 6rem 2rem 4rem;
  background-color: var(--dark-gray);
`;

const SectionTitle = styled.h2`
  font-family: 'Blackwood Castle', serif;
  font-size: 3rem;
  color: var(--gold);
  text-align: center;
  margin-bottom: 3rem;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
`;

const PricingContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 3rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const PricingCard = styled.div`
  width: 100%;
  max-width: 500px;
  background-color: var(--royal-blue);
  border: 2px solid var(--gold);
  border-radius: 5px;
  overflow: hidden;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.3);
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: -5px;
    left: -5px;
    right: -5px;
    bottom: -5px;
    border: 1px solid var(--gold);
    pointer-events: none;
    opacity: 0.5;
    z-index: 1;
    border-radius: 8px;
  }
`;

const CardHeader = styled.div`
  background-color: var(--gold);
  padding: 1.5rem;
  text-align: center;
`;

const CardTitle = styled.h3`
  font-family: 'Blackwood Castle', serif;
  font-size: 2rem;
  color: var(--royal-blue);
  margin: 0;
`;

const CardBody = styled.div`
  padding: 2rem;
`;

const HoursContainer = styled.div`
  margin-bottom: 2rem;
`;

const HoursTitle = styled.h4`
  font-family: 'Blackwood Castle', serif;
  font-size: 1.5rem;
  color: var(--gold);
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const HoursText = styled.p`
  color: var(--white);
  font-size: 1.1rem;
  line-height: 1.6;
`;

const PriceList = styled.div`
  margin-top: 2rem;
`;

const PriceTitle = styled.h4`
  font-family: 'Blackwood Castle', serif;
  font-size: 1.5rem;
  color: var(--gold);
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const PriceCategory = styled.h5`
  font-family: 'Blackwood Castle', serif;
  font-size: 1.2rem;
  color: var(--gold);
  margin: 1.5rem 0 0.5rem;
  border-bottom: 1px solid var(--gold);
  padding-bottom: 0.5rem;
`;

const PriceItem = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  padding: 0.5rem 0;
  border-bottom: 1px dotted rgba(197, 80, 0, 0.3);
`;

const PriceItemName = styled.span`
  color: var(--white);
`;

const PriceItemValue = styled.span`
  color: var(--gold);
  font-weight: bold;
`;

const TattooNote = styled.p`
  color: var(--white);
  font-style: italic;
  margin-top: 1.5rem;
  padding: 1rem;
  background-color: rgba(0, 0, 0, 0.2);
  border-left: 3px solid var(--gold);
  line-height: 1.6;
`;

const PiercingImage = styled.img`
  width: 100%;
  max-width: 400px;
  margin: 2rem auto;
  display: block;
  border: 2px solid var(--gold);
  border-radius: 5px;
`;

const AuthorisationButton = styled.a`
  display: block;

  max-width: 85%;
  max-height: 80vh;
  aspect-ratio: 0.707 / 1; /* Maintenir le ratio A4 même quand redimensionné */
  margin: 2rem auto;
  padding: 0;
  border: 2px solid var(--gold);
  border-radius: 5px;
  color: var(--gold);
  font-family: 'Blackwood Castle', serif;
  font-size: 1.5rem;
  text-decoration: none;
  transition: all 0.3s ease;
  position: relative;
  background-image: url(${require('../../assets/autorisation_parentale_preview.jpg')});
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  overflow: hidden;
  animation: pulsate 2s infinite;

  /* Effet de brillance (shine) */
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.7);
    z-index: 1;
    transition: background 0.3s ease;
  }

  /* Effet de brillance qui traverse le bouton */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 50%;
    height: 100%;
    background: linear-gradient(
      to right,
      rgba(255, 215, 0, 0) 0%,
      rgba(255, 215, 0, 0.3) 50%,
      rgba(255, 215, 0, 0) 100%
    );
    transform: skewX(-25deg);
    z-index: 4;
    animation: authShine 3s infinite;
  }

  span {
    position: relative;
    z-index: 2;
    padding: 1.2rem;
    background: rgba(0, 0, 0, 0.75);
    border: 2px solid var(--gold);
    border-radius: 4px;
    box-shadow: 0 0 15px var(--gold);
    animation: glowPulse 2s infinite alternate;
    letter-spacing: 1px;
    text-shadow: 0 0 10px var(--gold), 0 0 15px var(--gold);
    font-size: 1.8rem;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);

    &::after {
      background: rgba(0, 0, 0, 0.5);
    }

    span {
      background: var(--gold);
      color: var(--royal-blue);
    }
  }

  /* Double bordure décorative */
  &::after {
    content: '';
    position: absolute;
    top: -5px;
    left: -5px;
    right: -5px;
    bottom: -5px;
    border: 1px solid var(--gold);
    pointer-events: none;
    opacity: 0.5;
    z-index: 3;
    border-radius: 8px;
  }

  @keyframes authShine {
    0% {
      left: -100%;
    }
    20% {
      left: 100%;
    }
    100% {
      left: 100%;
    }
  }

  @keyframes glowPulse {
    0% {
      box-shadow: 0 0 10px var(--gold);
      text-shadow: 0 0 5px var(--gold), 0 0 10px var(--gold);
    }
    100% {
      box-shadow: 0 0 25px var(--gold), 0 0 40px var(--gold);
      text-shadow: 0 0 15px var(--gold), 0 0 25px var(--gold);
    }
  }

  @keyframes pulsate {
    0% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.02);
    }
    100% {
      transform: scale(1);
    }
  }
`;

const Pricing = () => {
  return (
    <PricingSection id="tarifs">
      <SectionTitle>Tarifs & Horaires</SectionTitle>
      <PricingContainer>
        {/* Tattoo Card */}
        <PricingCard>
          <CardHeader>
            <CardTitle>Tatouages</CardTitle>
          </CardHeader>
          <CardBody>
            <HoursContainer>
              <HoursTitle>
                <FaClock /> Horaires
              </HoursTitle>
              <HoursText>Lun - Dim<br />11h00 - 20h00</HoursText>
            </HoursContainer>

            <PriceList>
              <PriceTitle>
                <FaEuroSign /> Tarifs
              </PriceTitle>
              <PriceItem>
                <PriceItemName>Ouverture d'aiguille</PriceItemName>
                <PriceItemValue>40€</PriceItemValue>
              </PriceItem>
              <TattooNote>
                Pour obtenir un devis précis pour votre projet de tatouage, veuillez contacter directement l'artiste via Instagram avec votre projet. Le prix final dépendra de la taille, de la complexité et du temps nécessaire pour réaliser votre tatouage. Si vous avez besoin d'une autorisation parentale, vous pouvez télécharger le formulaire ci-dessous.
              </TattooNote>
              <AuthorisationButton 
                href={require('../../assets/autorisation_parentale.pdf')} 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <span>Autorisation Parentale</span>
              </AuthorisationButton>
            </PriceList>
          </CardBody>
        </PricingCard>

        {/* Piercing Card */}
        <PricingCard>
          <CardHeader>
            <CardTitle>Piercings</CardTitle>
          </CardHeader>
          <CardBody>
            <HoursContainer>
              <HoursTitle>
                <FaClock /> Horaires
              </HoursTitle>
              <HoursText>Mar - Sam<br />14h00 - 19h00</HoursText>
            </HoursContainer>

            <PriceList>
              <PriceTitle>
                <FaEuroSign /> Tarifs
              </PriceTitle>
              
              <PriceCategory>Oreille</PriceCategory>
              <PriceItem>
                <PriceItemName>Lobe</PriceItemName>
                <PriceItemValue>20€</PriceItemValue>
              </PriceItem>
              <PriceItem>
                <PriceItemName>Helix</PriceItemName>
                <PriceItemValue>20€</PriceItemValue>
              </PriceItem>
              <PriceItem>
                <PriceItemName>Tragus</PriceItemName>
                <PriceItemValue>20€</PriceItemValue>
              </PriceItem>
              <PriceItem>
                <PriceItemName>Anti-helix</PriceItemName>
                <PriceItemValue>20€</PriceItemValue>
              </PriceItem>
              <PriceItem>
                <PriceItemName>Flat</PriceItemName>
                <PriceItemValue>20€</PriceItemValue>
              </PriceItem>
              <PriceItem>
                <PriceItemName>Rook</PriceItemName>
                <PriceItemValue>20€</PriceItemValue>
              </PriceItem>
              <PriceItem>
                <PriceItemName>Conch</PriceItemName>
                <PriceItemValue>20€</PriceItemValue>
              </PriceItem>
              <PriceItem>
                <PriceItemName>Daith</PriceItemName>
                <PriceItemValue>30€</PriceItemValue>
              </PriceItem>
              <PriceItem>
                <PriceItemName>Snug</PriceItemName>
                <PriceItemValue>20€</PriceItemValue>
              </PriceItem>
              <PriceItem>
                <PriceItemName>Industriel</PriceItemName>
                <PriceItemValue>30€</PriceItemValue>
              </PriceItem>

              <PriceCategory>Visage</PriceCategory>
              <PriceItem>
                <PriceItemName>Arcade</PriceItemName>
                <PriceItemValue>40€</PriceItemValue>
              </PriceItem>
              <PriceItem>
                <PriceItemName>Nostril</PriceItemName>
                <PriceItemValue>25€</PriceItemValue>
              </PriceItem>
              <PriceItem>
                <PriceItemName>Contour de bouche</PriceItemName>
                <PriceItemValue>50€</PriceItemValue>
              </PriceItem>
              <PriceItem>
                <PriceItemName>Langue</PriceItemName>
                <PriceItemValue>50€</PriceItemValue>
              </PriceItem>
              <PriceItem>
                <PriceItemName>Smiley</PriceItemName>
                <PriceItemValue>30€</PriceItemValue>
              </PriceItem>
              <PriceItem>
                <PriceItemName>Labret</PriceItemName>
                <PriceItemValue>40€</PriceItemValue>
              </PriceItem>
              <PriceItem>
                <PriceItemName>Labret vertical</PriceItemName>
                <PriceItemValue>45€</PriceItemValue>
              </PriceItem>
              <PriceItem>
                <PriceItemName>Medusa</PriceItemName>
                <PriceItemValue>40€</PriceItemValue>
              </PriceItem>

              <PriceCategory>Corps</PriceCategory>
              <PriceItem>
                <PriceItemName>Nombril</PriceItemName>
                <PriceItemValue>30€</PriceItemValue>
              </PriceItem>
              <PriceItem>
                <PriceItemName>Un téton</PriceItemName>
                <PriceItemValue>30€</PriceItemValue>
              </PriceItem>
              <PriceItem>
                <PriceItemName>Deux téton</PriceItemName>
                <PriceItemValue>50€</PriceItemValue>
              </PriceItem>
              <PriceItem>
                <PriceItemName>Surface</PriceItemName>
                <PriceItemValue>40€</PriceItemValue>
              </PriceItem>
              <PriceItem>
                <PriceItemName>Un microdermal</PriceItemName>
                <PriceItemValue>50€</PriceItemValue>
              </PriceItem>
              <PriceItem>
                <PriceItemName>Deux microdermal</PriceItemName>
                <PriceItemValue>80€</PriceItemValue>
              </PriceItem>
            </PriceList>
            

          </CardBody>
        </PricingCard>
      </PricingContainer>
    </PricingSection>
  );
};

export default Pricing;
